// Import axios - library for making HTTP requests
import axios from "axios";

// API URL: Use environment variable in production, localhost in development
const API_URL = process.env.VUE_APP_API_URL || "http://localhost:8000";

// Create an axios instance with default configuration
const api = axios.create({
    baseURL: API_URL
});

// INTERCEPTOR: Automatically add JWT token to every request
// This runs BEFORE each request is sent
api.interceptors.request.use(config => {
    // Get token from browser"s localStorage
    const token = localStorage.getItem("token");

    // If token exists, add it to Authorization header
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

// Add this AFTER your existing request interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem("refreshToken");
                if (!refreshToken) throw new Error("No refresh token");

                const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
                    refresh_token: refreshToken
                });

                localStorage.setItem("token", data.token);
                originalRequest.headers.Authorization = `Bearer ${data.token}`;
                return api(originalRequest);

            } catch (refreshError) {
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("user");
                alert("Session expired. Please log in again.");
                window.location.href = "/";
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Export an object with all our API functions
export default {
    // ==================== AUTH ENDPOINTS ====================

    /**
     * Login user
     * @param {string} email - User"s email
     * @param {string} password - User"s password
     * @returns {Promise} Response with user_id, access_token
     */
    login(email, password) {
        return api.post("/api/auth/login", { email, password });
    },
    
    /**
     * Register new user
     * @param {Object} userData - User registration data
     * @param {string} userData.email
     * @param {string} userData.password
     * @param {string} userData.full_name
     * @returns {Promise} Response with user data
     */
    register(userData) {
        return api.post("/api/auth/register", userData);
    },

    /**
     * Update user profile
     * @param {Object} profileData - Profile update data
     * @param {string} profileData.full_name - New full name (optional)
     * @param {string} profileData.email - New email (optional)
     * @param {string} profileData.school - New school (optional)
     * @returns {Promise} Response with updated user data
     */
    updateProfile(profileData) {
        return api.put("/api/auth/profile", profileData);
    },

        // ==================== SETTINGS ENDPOINTS ====================

    /**
     * Get all user settings
     * @returns {Promise} Complete settings object with all categories
     * @example
     * const { data } = await api.getSettings();
     * // data = { appearance: {...}, notifications: {...}, privacy: {...}, learning: {...} }
     */
    getSettings() {
        return api.get("/api/auth/settings");
    },

    /**
     * Update user settings (supports partial updates)
     * @param {Object} updates - Settings to update (only include what you want to change)
     * @returns {Promise} Response with complete updated settings
     * @example
     * // Update only theme
     * await api.updateSettings({ appearance: { theme: "light" } });
     *
     * // Update multiple categories
     * await api.updateSettings({
     *   appearance: { theme: "dark", font_size: "large" },
     *   notifications: { send_notifications: false }
     * });
     */
    updateSettings(updates) {
        return api.put("/api/auth/settings", updates);
    },

    /**
     * Reset all settings to defaults
     * @returns {Promise} Response with default settings
     */
    resetSettings() {
        return api.post("/api/auth/settings/reset");
    },

    /**
     * Change user password
     * @param {Object} passwordData - Password change data
     * @param {string} passwordData.current_password - Current password
     * @param {string} passwordData.new_password - New password
     * @returns {Promise} Response with success message
     */
    changePassword(passwordData) {
        return api.put("/api/auth/password", passwordData);
    },

    /**
     * Get current authenticated user"s info
     * @returns {Promise} Response with user data
     */
    getCurrentUser() {
        return api.get("/api/auth/me");
    },
        
    // ==================== RATE LIMITING ENDPOINTS ====================

    /**
     * Check if user/guest can submit more questions today
     * @param {Object} data - Request data
     * @param {string} data.fingerprint - Device fingerprint for guests
     * @param {number} data.user_id - User ID for authenticated users
     * @returns {Promise} Response with limit info
     */
    checkQuestionLimit(data) {
        return api.post("/api/questions/check-limit", data);
    },

    /**
     * Check what features a user/guest can access
     * @param {Object} data - Request data
     * @param {string} data.fingerprint - Device fingerprint
     * @param {number} data.user_id - User ID (optional)
     * @returns {Promise} Response with feature access info
     */
    checkFeatureAccess(data) {
        return api.post("/api/questions/feature-access", data);
    },

    // ==================== QUESTIONS ENDPOINTS ====================

    /**
     * Get available filter options from the backend
     * @returns {Promise} Response with filter options
     */
    getFilterOptions() {
        return api.get("/api/questions/filters/options");
    },

    /**
     * Get list of questions with optional filters
     * @param {Object} filters - Optional query parameters
     * @param {Array<string>} filters.topics - Array of topic values
     * @param {Array<string>} filters.difficulties - Array of difficulty values  
     * @param {Array<string>} filters.question_types - Array of question type values
     * @param {number} filters.limit - Max number of questions
     * @param {number} filters.user_id - User ID for personalization
     * @returns {Promise} Response with array of questions
     */
    getQuestions(filters = {}) {
        const params = new URLSearchParams();
        
        if (filters.user_id !== undefined) {
            params.append('user_id', filters.user_id);
        }
        if (filters.limit) {
            params.append('limit', filters.limit);
        }
        
        // Handle array parameters for FastAPI
        if (filters.topics?.length) {
            filters.topics.forEach(t => params.append('topics', t));
        }
        if (filters.difficulties?.length) {
            filters.difficulties.forEach(d => params.append('difficulties', d));
        }
        if (filters.question_types?.length) {
            filters.question_types.forEach(t => params.append('question_types', t));
        }
        if (filters.filter_mode) {
            params.append('filter_mode', filters.filter_mode);
        }

        const queryString = params.toString();
        return api.get(`/api/questions/${queryString ? '?' + queryString : ''}`);
    },
    
    /**
     * Get a specific question by ID
     * @param {number} id - Question ID
     * @returns {Promise} Response with question data
     */
    getQuestion(id) {
        return api.get(`/api/questions/${id}`);
    },
    
    /**
     * Submit an answer for marking
     * @param {number} questionId - Question ID
     * @param {Object} data - Submission data
     * @param {number} data.user_id
     * @param {string} data.user_answer
     * @param {number} data.time_taken_seconds
     * @param {number} data.hints_used
     * @returns {Promise} Response with score and feedback
     */
    submitAnswer(questionId, data) {
        return api.post(`/api/questions/${questionId}/submit`, data);
    },
    
    // ==================== ANALYTICS ENDPOINTS ====================
    
    /**
     * Get user"s complete analytics dashboard
     * @param {number} userId - User ID
     * @returns {Promise} Response with analytics data
     */
    getUserAnalytics(userId, params = {}) {
        return api.get(`/api/analytics/users/${userId}/analytics`, { params });
    },
        
    /**
     * Get user"s attempt history
     * @param {number} userId - User ID
     * @param {number} limit - Max number of attempts to return
     * @returns {Promise} Response with attempt history
     */
    getUserAttempts(userId, limit = 10) {
        return api.get(`/api/analytics/users/${userId}/attempts`, {
            params: { limit }
        });
    },

    getQuestionAnalytics(questionId, limit = 10) {
        return api.get(`/api/questions/${questionId}/attempts`, {
            params: { limit }
        });
    },

    // ====================== USER INPUT ENDPOINTS =================
    /**
     * Send user's canvas image (chemical structure) to MathPix to
     * convert to SMILES/InChI notation
     * @param {binary} image - image
     * @returns {Promise} Response with SMILES/InChI data
     */
    processImage(image) {
        return api.post(`/api/input/structure`, image);
    },

    refresh(refresh_token) {
        return api.post(`/api/auth/refresh`, refresh_token);
    },

    // ==================== FEEDBACK ENDPOINTS ====================

    /**
     * Submit general feedback (email-only, no database storage)
     * @param {Object} feedbackData - Feedback data
     * @param {boolean} feedbackData.is_helpful - Rating (true=positive, false=negative, null=neutral)
     * @param {string} feedbackData.feedback_text - Feedback message
     * @param {string} feedbackData.feedback_type - Type: general, bug_report, feature_request, other
     * @param {Object} feedbackData.context - Contextual data (page, feature, etc.)
     * @param {string} feedbackData.contact_email - Contact email (optional, for guests)
     * @param {string} feedbackData.contact_name - Contact name (optional, for guests)
     * @returns {Promise} Response with success message
     */
    submitFeedback(feedbackData) {
        return api.post('/api/feedback/submit', feedbackData);
    },

    // ==================== PAYMENT ENDPOINTS ====================

    /**
     * Create a Stripe Checkout session for subscription purchase
     * @param {string} tier - Target tier ("TIER 1" or "TIER 2")
     * @param {string} billingCycle - Billing cycle ("monthly" or "annual")
     * @returns {Promise} Response with checkout_url and session_id
     */
    createCheckoutSession(tier, billingCycle = 'monthly') {
        return api.post('/api/payments/create-checkout-session', {
            tier,
            billing_cycle: billingCycle
        });
    },

    /**
     * Create a Stripe Customer Portal session for subscription management
     * Allows users to update payment method, cancel, change plan, view invoices
     * @returns {Promise} Response with portal_url
     */
    createPortalSession() {
        return api.post('/api/payments/create-portal-session');
    },

    /**
     * Get current subscription status from Stripe
     * @returns {Promise} Response with tier, status, current_period_end, cancel_at_period_end
     */
    getSubscriptionStatus() {
        return api.get('/api/payments/subscription-status');
    }

};